/**
 * A leitura que o Chrome derruba sozinho (TASK-11982).
 *
 * No Linux, o Chrome até a versão 155 herda o termios da porta. Se um programa
 * anterior (esptool, Arduino IDE, monitor pyserial) deixou `VMIN=0`, a primeira
 * leitura sem dado devolve 0 bytes e o Chrome a trata como aparelho perdido:
 * `NetworkError: The device has been lost`, com o cabo no lugar. O conserto é o
 * commit `45ccdf6e7af9` do Chromium, que só chega na versão 156. Medido em 29/09/2026 no
 * Robô II: a mesma rodada, no mesmo Chrome 154, falha com `min = 0` e grava
 * com `stty min 1`.
 *
 * O esptool-js engole esse erro no laço dele e a gravação termina em
 * "Failed to connect", que a tela lia como "segure o BOOT". Por isso a porta
 * passa por `vigiarLeitura` antes de chegar a qualquer leitor: quem conta o
 * que aconteceu é a porta, não a biblioteca.
 */
import type { PortaSerial } from './serial'

export function eLeituraPerdida(erro: unknown): boolean {
  if (!erro || typeof erro !== 'object') return false
  const { name, message } = erro as { name?: unknown; message?: unknown }
  return name === 'NetworkError' && typeof message === 'string' && /device has been lost/i.test(message)
}

/** A versão principal do Chrome/Edge, ou `null` fora deles. */
export function versaoDoChrome(ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent): number | null {
  const m = /\bChrom(?:e|ium)\/(\d+)\./.exec(ua)
  return m ? Number(m[1]) : null
}

/**
 * O que dizer quando a leitura caiu. `cabo` vem de `SerialPort.connected`:
 * `false` é o cabo que saiu de verdade; `true` ou sem o campo, é a leitura do
 * navegador que morreu com a porta ainda lá.
 */
export function motivoDaLeituraPerdida(cabo: boolean | undefined, chrome: number | null): string {
  if (cabo === false) {
    return 'O cabo USB se soltou durante a conexão. Ligue de novo e tente outra vez.'
  }
  const qual = chrome ? `o Chrome ${chrome}` : 'o navegador'
  const saida = chrome !== null && chrome >= 156
    ? 'Rode no terminal "stty -F /dev/ttyACM0 min 1" (troque pela porta do robô, se for outra) e tente de novo.'
    : 'Atualize o Chrome para a versão 156 ou mais nova, ou rode no terminal '
      + '"stty -F /dev/ttyACM0 min 1" (troque pela porta do robô, se for outra) e tente de novo.'
  return `A USB está ligada, mas ${qual} parou de ler a porta logo que ela abriu. `
    + 'Isso acontece no Linux depois de usar esptool, Arduino IDE ou monitor serial no robô. '
    + `A placa não tem defeito e não precisa segurar o BOOT. ${saida}`
}

/**
 * A mesma porta, com cada `read()` observado. O primeiro erro de leitura
 * perdida vai para `aoPerder`; o erro segue para o leitor como antes.
 *
 * Os métodos são chamados sobre o objeto verdadeiro: a Web Serial confere o
 * `this` e recusa um Proxy ("Illegal invocation").
 */
export function vigiarLeitura(porta: PortaSerial, aoPerder: (erro: unknown) => void): PortaSerial {
  const ligar = <T extends object>(alvo: T, trocar: (chave: PropertyKey) => unknown) =>
    new Proxy(alvo, {
      get(t, chave) {
        const trocado = trocar(chave)
        if (trocado !== undefined) return trocado
        const v = Reflect.get(t, chave, t)
        return typeof v === 'function' ? v.bind(t) : v
      },
    })
  const leitor = (r: ReadableStreamDefaultReader<Uint8Array>) => ligar(r, (chave) =>
    chave === 'read'
      ? () => r.read().catch((erro: unknown) => {
        if (eLeituraPerdida(erro)) aoPerder(erro)
        throw erro
      })
      : undefined)
  return ligar(porta, (chave) => {
    if (chave !== 'readable') return undefined
    const fluxo = porta.readable
    if (!fluxo) return null
    return ligar(fluxo, (k) => k === 'getReader'
      ? (...args: unknown[]) => leitor((fluxo.getReader as (...a: unknown[]) => ReadableStreamDefaultReader<Uint8Array>)(...args))
      : undefined)
  })
}
